import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type {
  DnsRecordDto,
  EmailDnsCheck,
  EmailDnsLevel,
  OwnedDomainDetail,
} from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Button, Card, ErrorNotice } from "../../components/form.js";

/** Everything about one owned domain: registrar settings, nameservers, email setup, DNS. */
export function DomainPage() {
  const { t, i18n } = useTranslation();
  const { domainId } = useParams({ from: "/admin/domains/$domainId" });
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["domains", domainId, "detail"],
    queryFn: () => api.domains.detail(domainId),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["domains"] });
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  const d = detail.data;

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-mono text-[22px] text-ink">{d?.name ?? "…"}</h1>
          {d ? (
            <p className="text-sm text-ink-2">
              {t(`domains.status.${d.status}`)}
              {d.expiresAt ? ` · ${t("domains.expires", { date: format(d.expiresAt) })}` : ""}
              {d.source === "external" ? ` · ${t("domains.connected")}` : ` · ${d.provider}`}
            </p>
          ) : null}
        </div>
        <Link
          to="/admin/domains"
          className="text-sm text-accent underline-offset-2 hover:underline"
        >
          {t("admin.nav.domains")}
        </Link>
      </div>
      <ErrorNotice error={detail.error} />
      {d && d.source === "registered" ? (
        <>
          <SettingsCard domain={d} onChanged={refresh} />
          <EmailSetupCard domain={d} onChanged={refresh} format={format} />
          {d.mailSetupAt ? <EmailDnsCard domainId={d.id} format={format} /> : null}
          <NameserversCard domain={d} onChanged={refresh} />
          <RecordsCard records={d.records} />
          <TransferCard domain={d} />
        </>
      ) : null}
      {d && d.source === "external" ? (
        <>
          {d.status === "pending_verification" ? (
            <VerifyCard domainId={d.id} onVerified={refresh} />
          ) : (
            <EmailDnsCard domainId={d.id} format={format} />
          )}
          <Card title={t("domain.external.title")}>
            <p className="text-sm text-ink-2">{t("domain.external.description")}</p>
          </Card>
          <DisconnectCard domain={d} />
        </>
      ) : null}
    </section>
  );
}

function SettingsCard({
  domain: d,
  onChanged,
}: {
  domain: OwnedDomainDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const update = useMutation({
    mutationFn: (change: { autoRenew?: boolean; locked?: boolean; whoisPrivacy?: boolean }) =>
      api.domains.updateSettings(d.id, change),
    onSuccess: onChanged,
  });
  const rows = [
    {
      key: "autoRenew",
      value: d.autoRenew,
      label: t("domain.autoRenew"),
      hint: t("domains.autoRenewHint"),
    },
    { key: "locked", value: d.locked, label: t("domain.lock"), hint: t("domain.lockHint") },
    {
      key: "whoisPrivacy",
      value: d.whoisPrivacy,
      label: t("domain.whois"),
      hint: t("domain.whoisHint"),
    },
  ] as const;
  return (
    <Card title={t("domain.settings")}>
      <div className="divide-y divide-rule">
        {rows.map((row) => (
          <label key={row.key} className="flex items-start justify-between gap-4 py-3 text-sm">
            <span>
              <span className="block font-medium text-ink">{row.label}</span>
              <span className="block text-xs text-ink-3">{row.hint}</span>
            </span>
            <input
              type="checkbox"
              className="mt-1"
              checked={row.value}
              disabled={update.isPending}
              onChange={(e) => update.mutate({ [row.key]: e.target.checked })}
            />
          </label>
        ))}
      </div>
      <ErrorNotice error={update.error} />
    </Card>
  );
}

function EmailSetupCard({
  domain: d,
  onChanged,
  format,
}: {
  domain: OwnedDomainDetail;
  onChanged: () => void;
  format: (iso: string) => string;
}) {
  const { t } = useTranslation();
  const setup = useMutation({
    mutationFn: () => api.domains.setupEmail(d.id),
    onSuccess: onChanged,
  });
  return (
    <Card title={t("domain.email.title")}>
      <p className="text-sm text-ink-2">{t("domain.email.description")}</p>
      <div className="flex flex-wrap items-center gap-3">
        {d.mailSetupAt ? (
          <span className="rounded bg-good-soft px-2 py-0.5 text-xs text-good">
            {t("domain.email.done", { date: format(d.mailSetupAt) })}
          </span>
        ) : (
          <span className="rounded bg-warn-soft px-2 py-0.5 text-xs text-warn">
            {t("domain.email.notYet")}
          </span>
        )}
        <Button
          variant={d.mailSetupAt ? "secondary" : "primary"}
          disabled={setup.isPending}
          onClick={() => setup.mutate()}
        >
          {d.mailSetupAt ? t("domain.email.reapply") : t("domain.email.apply")}
        </Button>
      </div>
      <ErrorNotice error={setup.error} />
      {setup.data ? (
        <p className="text-xs text-ink-3">
          {t("domain.email.result", {
            created: setup.data.created,
            updated: setup.data.updated,
            unchanged: setup.data.unchanged,
          })}
        </p>
      ) : null}
    </Card>
  );
}

function NameserversCard({
  domain: d,
  onChanged,
}: {
  domain: OwnedDomainDetail;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const ours = d.zone?.nameservers ?? [];
  const [text, setText] = useState(d.registrarNameservers.join("\n"));
  const save = useMutation({
    mutationFn: (nameservers: string[]) => api.domains.setNameservers(d.id, nameservers),
    onSuccess: onChanged,
  });
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const list = text
      .split(/[\s,]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    save.mutate(list);
  };
  const usingOurs = ours.length > 0 && ours.every((ns) => d.registrarNameservers.includes(ns));
  return (
    <Card title={t("domain.nameservers.title")}>
      <p className="text-sm text-ink-2">
        {usingOurs ? t("domain.nameservers.ours") : t("domain.nameservers.external")}
      </p>
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <textarea
          className="min-h-24 w-full rounded-md border border-rule bg-surface px-3 py-2 font-mono text-sm outline-none focus:border-accent"
          value={text}
          onChange={(e) => setText(e.target.value)}
          spellCheck={false}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending}>
            {t("common.save")}
          </Button>
          {ours.length > 0 ? (
            <Button variant="secondary" onClick={() => setText(ours.join("\n"))}>
              {t("domain.nameservers.useOurs")}
            </Button>
          ) : null}
        </div>
        <ErrorNotice error={save.error} />
      </form>
    </Card>
  );
}

function RecordsCard({ records }: { records: DnsRecordDto[] }) {
  const { t } = useTranslation();
  return (
    <Card title={t("domain.records.title")}>
      {records.length === 0 ? (
        <p className="text-sm text-ink-3">{t("domain.records.none")}</p>
      ) : null}
      {records.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wider text-ink-3">
                <th className="py-1 pr-3">Type</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Value</th>
                <th className="py-1 pr-3">TTL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {records.map((r) => (
                <tr key={r.id ?? `${r.type}-${r.name}-${r.value}`}>
                  <td className="py-1 pr-3 text-accent">{r.type}</td>
                  <td className="py-1 pr-3">{r.name}</td>
                  <td className="break-all py-1 pr-3 text-ink-2">
                    {r.priority !== undefined ? `${r.priority} ` : ""}
                    {r.value}
                  </td>
                  <td className="py-1 pr-3 text-ink-3">{r.ttl}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

function TransferCard({ domain: d }: { domain: OwnedDomainDetail }) {
  const { t } = useTranslation();
  const code = useMutation({ mutationFn: () => api.domains.authCode(d.id) });
  return (
    <Card title={t("domain.transfer.title")}>
      <p className="text-sm text-ink-2">{t("domain.transfer.description")}</p>
      {d.locked ? (
        <p className="text-xs text-ink-3">{t("domain.transfer.unlockFirst")}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" disabled={code.isPending} onClick={() => code.mutate()}>
            {t("domain.transfer.reveal")}
          </Button>
          {code.data ? (
            <code className="rounded bg-surface-2 px-2 py-1 font-mono text-sm">
              {code.data.authCode}
            </code>
          ) : null}
        </div>
      )}
      <ErrorNotice error={code.error} />
    </Card>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="shrink-0 rounded border border-rule px-2 py-0.5 text-xs text-ink-2 hover:bg-surface-2"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? t("domain.verify.copied") : t("domain.verify.copy")}
    </button>
  );
}

/** The ownership TXT record for a connected domain, and a button that looks it up now. */
function VerifyCard({ domainId, onVerified }: { domainId: string; onVerified: () => void }) {
  const { t } = useTranslation();
  const verification = useQuery({
    queryKey: ["domains", domainId, "verification"],
    queryFn: () => api.domains.verification(domainId),
  });
  const check = useMutation({
    mutationFn: () => api.domains.verify(domainId),
    onSuccess: (result) => {
      if (result.verified) onVerified();
    },
  });
  const v = check.data ?? verification.data;
  return (
    <Card title={t("domain.verify.title")}>
      <p className="text-sm text-ink-2">{t("domain.verify.description")}</p>
      <ErrorNotice error={verification.error} />
      {v ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md bg-surface-2 p-3 text-sm">
          <dt className="text-ink-3">{t("domain.verify.type")}</dt>
          <dd className="font-mono">{v.record.type}</dd>
          <dt className="text-ink-3">{t("domain.verify.name")}</dt>
          <dd className="flex flex-col gap-1">
            <span className="flex items-center gap-2 font-mono">
              <span className="break-all">{v.record.name}</span>
              <CopyButton value={v.record.name} />
            </span>
            <span className="text-xs text-ink-3">
              {t("domain.verify.fullName", { fqdn: v.record.fqdn })}
            </span>
          </dd>
          <dt className="text-ink-3">{t("domain.verify.value")}</dt>
          <dd className="flex items-center gap-2 font-mono">
            <span className="break-all">{v.record.value}</span>
            <CopyButton value={v.record.value} />
          </dd>
        </dl>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={check.isPending} onClick={() => check.mutate()}>
          {check.isPending ? t("common.loading") : t("domain.verify.check")}
        </Button>
        {check.data && !check.data.verified ? (
          <span className="text-sm text-warn">
            {check.data.found.length === 0
              ? t("domain.verify.notFound", { fqdn: check.data.record.fqdn })
              : t("domain.verify.foundOther", {
                  fqdn: check.data.record.fqdn,
                  values: check.data.found.join(", "),
                })}
          </span>
        ) : null}
      </div>
      <ErrorNotice error={check.error} />
    </Card>
  );
}

type CheckedRecord = EmailDnsCheck["records"][number];

const CHECK_TONE: Record<CheckedRecord["status"], string> = {
  ok: "bg-good-soft text-good",
  missing: "bg-warn-soft text-warn",
  mismatch: "bg-danger-soft text-danger",
};

const LEVELS: EmailDnsLevel[] = ["required", "recommended", "optional"];

/** The records of a check grouped by level, most important first; empty levels are left out. */
export function byLevel(
  records: CheckedRecord[],
): Array<{ level: EmailDnsLevel; records: CheckedRecord[] }> {
  return LEVELS.map((level) => ({
    level,
    records: records.filter((r) => r.level === level),
  })).filter((group) => group.records.length > 0);
}

/** What the email template needs in DNS and what public DNS answers right now. */
function EmailDnsCard({ domainId, format }: { domainId: string; format: (iso: string) => string }) {
  const { t } = useTranslation();
  const check = useQuery({
    queryKey: ["domains", domainId, "email-dns-check"],
    queryFn: () => api.domains.emailDnsCheck(domainId),
  });
  const c = check.data;
  return (
    <Card title={t("domain.dnsCheck.title")}>
      <p className="text-sm text-ink-2">{t("domain.dnsCheck.description")}</p>
      <div className="flex flex-wrap items-center gap-3">
        {c ? (
          <span
            className={`rounded px-2 py-0.5 text-xs ${c.ready ? "bg-good-soft text-good" : "bg-warn-soft text-warn"}`}
          >
            {c.ready ? t("domain.dnsCheck.ready") : t("domain.dnsCheck.notReady")}
          </span>
        ) : null}
        {c && c.recommendedMissing > 0 ? (
          <span className="rounded bg-surface-2 px-2 py-0.5 text-xs text-ink-2">
            {t("domain.dnsCheck.recommendedMissing", { count: c.recommendedMissing })}
          </span>
        ) : null}
        <Button variant="secondary" disabled={check.isFetching} onClick={() => check.refetch()}>
          {check.isFetching ? t("common.loading") : t("domain.dnsCheck.check")}
        </Button>
        {c ? (
          <span className="text-xs text-ink-3">
            {t("domain.dnsCheck.checked", { date: format(c.checkedAt) })}
          </span>
        ) : null}
      </div>
      <ErrorNotice error={check.error} />
      {c
        ? byLevel(c.records).map((group) => (
            <section key={group.level} className="flex flex-col gap-1">
              <h3 className="text-sm font-medium text-ink">
                {t(`domain.dnsCheck.level.${group.level}`)}
              </h3>
              <p className="text-xs text-ink-3">{t(`domain.dnsCheck.levelHint.${group.level}`)}</p>
              <ul className="divide-y divide-rule">
                {group.records.map((r) => (
                  <li
                    key={`${r.type}-${r.fqdn}-${r.expected}`}
                    className="flex flex-col gap-1 py-2 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 ${CHECK_TONE[r.status]}`}>
                        {t(`domain.dnsCheck.status.${r.status}`)}
                      </span>
                      <span className="font-mono text-accent">{r.type}</span>
                      <span className="break-all font-mono">{r.fqdn}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="break-all font-mono text-ink-2">{r.expected}</span>
                      <CopyButton value={r.expected} />
                    </div>
                    {r.status === "mismatch" ? (
                      <span className="break-all font-mono text-ink-3">
                        {t("domain.dnsCheck.found", { values: r.found.join(" | ") })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))
        : null}
    </Card>
  );
}

function DisconnectCard({ domain: d }: { domain: OwnedDomainDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const disconnect = useMutation({
    mutationFn: () => api.domains.disconnect(d.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["domains"] });
      await navigate({ to: "/admin/domains" });
    },
  });
  return (
    <Card title={t("domain.disconnect.title")}>
      <p className="text-sm text-ink-2">{t("domain.disconnect.description")}</p>
      <div>
        <Button
          variant="danger"
          disabled={disconnect.isPending}
          onClick={() => {
            if (window.confirm(t("domain.disconnect.confirm", { domain: d.name }))) {
              disconnect.mutate();
            }
          }}
        >
          {t("domain.disconnect.button")}
        </Button>
      </div>
      <ErrorNotice error={disconnect.error} />
    </Card>
  );
}
